import * as React from 'react';
import {
  ChatReveal,
  AddTile,
  Button,
  Card,
  CardBody,
  DetailField,
  Divider,
  Field,
  Inline,
  Input,
  OptionSheet,
  PriorityTag,
  Stack,
  StatusChip,
  Text,
  Textarea,
  WeightClassBars,
} from '@relay-towing/ui';
import { PencilSimple } from '@phosphor-icons/react';
import type { Chat, EditKey } from './chat';
import {
  PRIORITY_LABEL,
  WEIGHT_LABEL,
  fmtReceived,
  splitPickup,
  vehicleText,
  type DraftCall,
  type DraftPriority,
  type DraftWeight,
} from './parseCall';

type TextKey = Exclude<EditKey, 'priority' | 'towType'>;

interface FieldDef {
  key: TextKey | 'received';
  label: string;
  optional?: boolean;
}

/** The Details section's rows, in the prototype's order. */
const DETAILS: FieldDef[] = [
  { key: 'contactPhone', label: 'Phone' },
  { key: 'received', label: 'Received' },
  { key: 'reason', label: 'Reason' },
  { key: 'dispatchNumber', label: 'Event #', optional: true },
  { key: 'contactName', label: 'Contact', optional: true },
  { key: 'plate', label: 'Plate', optional: true },
  { key: 'vin', label: 'VIN', optional: true },
];

const LABEL: Record<TextKey, string> = {
  pickup: 'Pickup',
  destination: 'Drop-off',
  vehicle: 'Vehicle',
  contactPhone: 'Phone',
  reason: 'Reason',
  dispatchNumber: 'Event #',
  contactName: 'Contact',
  plate: 'Plate',
  vin: 'VIN',
};

const PLACEHOLDER: Partial<Record<TextKey, string>> = {
  pickup: 'An address or an intersection',
  destination: 'An address or your yard',
  vehicle: 'Year, make and model',
};

/** What a text field holds now, as its box starts. */
function valueOf(call: DraftCall, key: TextKey): string {
  if (key === 'vehicle') return vehicleText(call);
  return call[key];
}

export interface CardActions {
  edit: (key: EditKey | null) => void;
  save: (key: TextKey, value: string) => void;
  setPriority: (priority: DraftPriority) => void;
  setWeight: (weight: DraftWeight) => void;
  show: (key: EditKey) => void;
  editNotes: (open: boolean) => void;
  saveNotes: (notes: string[]) => void;
  setAccount: (accountId: string) => void;
}

/** A field open for editing: its box, then Save and Cancel. */
function EditRow({
  label,
  initial,
  placeholder,
  onSave,
  onCancel,
}: {
  label: string;
  initial: string;
  placeholder?: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = React.useState(initial);
  return (
    <Stack gap={2}>
      <Field label={label}>
        <Input
          autoFocus
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onSave(value);
            if (event.key === 'Escape') onCancel();
          }}
        />
      </Field>
      <Inline gap={2}>
        <Button variant="soft" pill size="lg" onClick={() => onSave(value)}>
          Save
        </Button>
        <Button variant="secondary" pill size="lg" onClick={onCancel}>
          Cancel
        </Button>
      </Inline>
    </Stack>
  );
}

/** One of a few answers, as a row of pills: priority, weight. */
function ChipPicker<T extends string>({
  label,
  options,
  value,
  onPick,
  onCancel,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onPick: (value: T) => void;
  onCancel: () => void;
}) {
  return (
    <Stack gap={2}>
      <Text as="span" variant="body-lg-strong" tone="muted">
        {label}
      </Text>
      <Inline gap={2} wrap>
        {options.map((option) => (
          <Button
            key={option.value}
            variant={option.value === value ? 'soft' : 'secondary'}
            pill
            size="md"
            aria-pressed={option.value === value}
            onClick={() => onPick(option.value)}
          >
            {option.label}
          </Button>
        ))}
        <Button variant="subtle" pill size="md" onClick={onCancel}>
          Cancel
        </Button>
      </Inline>
    </Stack>
  );
}

function PencilButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      variant="secondary"
      size="icon-lg"
      pill
      aria-label={`Edit ${label.toLowerCase()}`}
      onClick={onClick}
    >
      <PencilSimple />
    </Button>
  );
}

/** The weight word and its bars, as the calls list draws them. */
export function WeightFigure({ weight }: { weight: DraftWeight }) {
  if (!weight) return null;
  return (
    <Inline as="span" gap={2} align="end">
      <Text as="span" variant="body" tone="muted">
        {WEIGHT_LABEL[weight]}
      </Text>
      <WeightClassBars
        weightClass={weight.toLowerCase() as 'light' | 'medium' | 'heavy'}
      />
    </Inline>
  );
}

/**
 * The call being set up, as a card in the chat: the mobile call sheet in
 * small. Its priority and the sender's number, its status, the street as
 * its title over the town, its weight; then Details, Vehicle and Notes.
 * Every field is tapped to change it in place, until the call is made.
 */
export function CallDraftCard({
  chat,
  cardId,
  accounts,
  accountId,
  editable,
  actions,
  revealFrom = 0,
}: {
  chat: Chat;
  cardId: string;
  accounts: { value: string; label: string }[];
  accountId: string | null;
  editable: boolean;
  actions: CardActions;
  /** The reply's step the card arrives on; its sections follow, one a beat. */
  revealFrom?: number;
}) {
  const { call, notes, editing } = chat;
  const { street, city } = splitPickup(call.pickup);
  const editingKey = editable ? editing : null;

  const textRow = (key: TextKey, label: string, value: string) =>
    editingKey === key ? (
      <EditRow
        key={key}
        label={label}
        initial={valueOf(call, key)}
        placeholder={PLACEHOLDER[key]}
        onSave={(next) => actions.save(key, next)}
        onCancel={() => actions.edit(null)}
      />
    ) : (
      <DetailField
        key={key}
        label={label}
        value={value}
        action={
          editable ? (
            <PencilButton label={label} onClick={() => actions.edit(key)} />
          ) : undefined
        }
      />
    );

  const detailValue = (def: FieldDef) =>
    def.key === 'received'
      ? call.received
        ? fmtReceived(call.received)
        : ''
      : call[def.key as keyof DraftCall];

  const rows = DETAILS.filter(
    (def) => !def.optional || detailValue(def) || chat.shown[def.key as EditKey]
  );
  const asks = DETAILS.filter(
    (def) =>
      def.optional && !detailValue(def) && !chat.shown[def.key as EditKey]
  );

  return (
    <Card
      id={cardId}
      bordered
      radius="xl"
      elevation="flat"
      padding="md"
      aria-label="Call draft"
    >
      <ChatReveal step={revealFrom + 1}>
        <CardBody>
          <Stack gap={3}>
            <Inline justify="between" align="center">
              <Inline gap={2} align="center">
                <PriorityTag priority={call.priority} />
                {call.dispatchNumber ? (
                  <Text as="span" variant="body" tone="muted" numeric>
                    #{call.dispatchNumber}
                  </Text>
                ) : null}
              </Inline>
              <StatusChip
                label={chat.created ? 'Created' : 'Draft'}
                tone={chat.created ? 'success' : 'neutral'}
              />
            </Inline>
            <Inline justify="between" align="end">
              <Stack gap={1}>
                <Text as="h2" variant="h2">
                  {street || 'New call'}
                </Text>
                {city ? <Text tone="muted">{city}</Text> : null}
              </Stack>
              <WeightFigure weight={call.towType} />
            </Inline>
            {editingKey === 'pickup' ? (
              textRow('pickup', 'Pickup', call.pickup)
            ) : editable ? (
              <Inline>
                <Button
                  variant="subtle"
                  size="sm"
                  pill
                  onClick={() => actions.edit('pickup')}
                >
                  {call.pickup ? 'Edit pickup' : 'Add pickup'}
                </Button>
              </Inline>
            ) : null}
            {editingKey === 'priority' ? (
              <ChipPicker<DraftPriority>
                label="Priority"
                options={(['NORMAL', 'HIGH', 'URGENT'] as const).map((p) => ({
                  value: p,
                  label: PRIORITY_LABEL[p],
                }))}
                value={call.priority}
                onPick={actions.setPriority}
                onCancel={() => actions.edit(null)}
              />
            ) : null}
            {editingKey === 'towType' ? (
              <ChipPicker<DraftWeight>
                label="Weight class"
                options={(['LIGHT', 'MEDIUM', 'HEAVY'] as const).map((w) => ({
                  value: w,
                  label: WEIGHT_LABEL[w],
                }))}
                value={call.towType}
                onPick={actions.setWeight}
                onCancel={() => actions.edit(null)}
              />
            ) : null}
          </Stack>
        </CardBody>
      </ChatReveal>
      <ChatReveal step={revealFrom + 2}>
        <Divider />
        <CardBody>
          <Stack gap={3}>
            <Text as="h3" variant="h3">
              Details
            </Text>
            <Field
              label="Account"
              description={
                call.account ? `The text says ${call.account}.` : undefined
              }
            >
              <OptionSheet
                title="Account"
                searchable
                placeholder="Pick the account"
                options={accounts}
                value={accountId ?? undefined}
                onValueChange={actions.setAccount}
                disabled={!editable}
              />
            </Field>
            {rows.map((def) =>
              def.key === 'received' ? (
                <DetailField
                  key="received"
                  label="Received"
                  value={detailValue(def)}
                />
              ) : (
                textRow(def.key, def.label, detailValue(def))
              )
            )}
            {editable ? (
              <Inline gap={2} wrap>
                <Button
                  variant="secondary"
                  pill
                  size="md"
                  onClick={() => actions.edit('priority')}
                >
                  Priority
                </Button>
                <Button
                  variant="secondary"
                  pill
                  size="md"
                  onClick={() => actions.edit('towType')}
                >
                  Weight
                </Button>
                {asks.map((def) => (
                  <Button
                    key={def.key}
                    variant="secondary"
                    pill
                    size="md"
                    onClick={() => actions.show(def.key as EditKey)}
                  >
                    + {def.label}
                  </Button>
                ))}
              </Inline>
            ) : null}
          </Stack>
        </CardBody>
      </ChatReveal>
      <ChatReveal step={revealFrom + 3}>
        <Divider />
        <CardBody>
          <Stack gap={3}>
            <Text as="h3" variant="h3">
              Vehicle
            </Text>
            {vehicleText(call) || editingKey === 'vehicle' ? (
              textRow(
                'vehicle',
                LABEL.vehicle,
                vehicleText(call) + (call.model ? '' : ' · model not given')
              )
            ) : editable ? (
              <AddTile width="block" onClick={() => actions.edit('vehicle')}>
                Add vehicle
              </AddTile>
            ) : null}
            {call.destination || editingKey === 'destination' ? (
              textRow('destination', LABEL.destination, call.destination)
            ) : editable ? (
              <AddTile
                width="block"
                onClick={() => actions.edit('destination')}
              >
                Add drop-off
              </AddTile>
            ) : null}
          </Stack>
        </CardBody>
      </ChatReveal>
      <ChatReveal step={revealFrom + 4}>
        <Divider />
        <CardBody>
          <NotesSection
            notes={notes}
            editing={editable && chat.editingNotes}
            editable={editable}
            actions={actions}
          />
        </CardBody>
      </ChatReveal>
    </Card>
  );
}

function NotesSection({
  notes,
  editing,
  editable,
  actions,
}: {
  notes: string[];
  editing: boolean;
  editable: boolean;
  actions: CardActions;
}) {
  const [draft, setDraft] = React.useState(notes.join('\n'));
  React.useEffect(() => {
    if (editing) setDraft(notes.join('\n'));
  }, [editing, notes]);
  return (
    <Stack gap={3}>
      <Text as="h3" variant="h3">
        Notes
      </Text>
      {editing ? (
        <Stack gap={2}>
          <Field label="Notes, one per line">
            <Textarea
              autoFocus
              rows={4}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </Field>
          <Inline gap={2}>
            <Button
              variant="soft"
              pill
              size="lg"
              onClick={() =>
                actions.saveNotes(
                  draft
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean)
                )
              }
            >
              Save
            </Button>
            <Button
              variant="secondary"
              pill
              size="lg"
              onClick={() => actions.editNotes(false)}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      ) : notes.length ? (
        <Stack gap={2} align="start">
          {notes.map((note, index) => {
            const at = note.indexOf(': ');
            return (
              <Text key={index}>
                {at > 0 ? (
                  <>
                    <Text as="span" tone="muted">
                      {note.slice(0, at)} ·{' '}
                    </Text>
                    {note.slice(at + 2)}
                  </>
                ) : (
                  note
                )}
              </Text>
            );
          })}
          {editable ? (
            <Button
              variant="subtle"
              size="sm"
              pill
              onClick={() => actions.editNotes(true)}
            >
              Edit notes
            </Button>
          ) : null}
        </Stack>
      ) : editable ? (
        <AddTile width="block" onClick={() => actions.editNotes(true)}>
          Add note
        </AddTile>
      ) : (
        <Text tone="muted">No notes</Text>
      )}
    </Stack>
  );
}
